import { SYMPHONY_WHATSAPP_INBOUND_NUMBER } from '@wix/ai-scheduling-assistant-constants';
import { adjustToContrast } from './qr-design';
import { normalizePalette, type ProfilePalette, type PublicProfileCard } from './profile-link-card';

/**
 * The pure decisions behind the public profile page (`pages/me/[handle]/[[...context]].tsx`) — which route
 * a path is, which device is reading, which CTAs it gets in which order, and the URLs behind them. No DOM,
 * no runtime imports beyond constants, so every rule here is unit-tested (test/profile-link-view.test.ts).
 *
 * See docs/symphony-profile-links-design.md §4.1 (handles), §4.2 (the app button), §5.1 (layout), §5.4 (OG).
 */

/**
 * A handle: the vanity-code shape (`lib/referral-capture.ts`), case-insensitive. Lowercased before any use
 * — universal links bypass the links service's lowercasing, so `Ronny.Elkayam` must mean the same profile.
 */
const HANDLE_RE = /^(?!.*\.\.)[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/;
/** A context slug: one segment of `[a-z0-9-]{1,40}`, case-insensitive (§4.1). */
export const CONTEXT_RE = /^[a-z0-9-]{1,40}$/;

export interface ProfileRoute {
  handle: string;
  context?: string;
}

const single = (value: string | string[] | undefined): string | undefined => (Array.isArray(value) ? undefined : value);

/**
 * The route the Next params name, or null when the HANDLE is not a profile handle (that renders the same 404
 * as an unknown handle). The grammar is identical on web, iOS and Android:
 *
 * - handle: 3–30 of `[a-z0-9._-]`, a letter or digit at both ends, no `..`, lowercased;
 * - context: ONE segment of `[a-z0-9-]{1,40}`, lowercased;
 * - a context that is malformed, or followed by more segments, falls back to the MAIN profile — the same
 *   fallback an expired or unknown context gets (D7, §4.5), so a mangled link still reaches the person.
 */
/**
 * The shortest handle an owner may CLAIM. Resolution (`parseProfileRoute`) keeps accepting 3+: a handle claimed
 * before the floor rose to 5 keeps working. The service enforces the same floor (VMR `VanityReferralCodes`).
 */
export const HANDLE_CLAIM_MIN = 5;

/** The lowercased handle when it may be CLAIMED: the route grammar plus the claim floor. */
export function claimableHandle(raw: string): string | null {
  const route = parseProfileRoute(raw.trim().toLowerCase(), undefined);
  return route && route.handle.length >= HANDLE_CLAIM_MIN ? route.handle : null;
}

export function parseProfileRoute(handleParam: string | string[] | undefined, contextParam: string | string[] | undefined): ProfileRoute | null {
  const handle = single(handleParam)?.toLowerCase();
  if (!handle || !HANDLE_RE.test(handle)) return null;
  const segments = contextParam === undefined ? [] : Array.isArray(contextParam) ? contextParam : [contextParam];
  if (segments.length !== 1) return { handle };
  const context = segments[0].toLowerCase();
  return CONTEXT_RE.test(context) ? { handle, context } : { handle };
}

export type ProfileDevice = 'phone' | 'desktop';

/**
 * A best guess from the user agent (§5.1) — it only ORDERS the CTAs, both of which work everywhere, so a
 * wrong guess costs a reader one extra glance, never a dead end. A tablet counts as a phone: it can open
 * WhatsApp and the app, and cannot scan its own screen.
 */
export function profileDeviceFor(userAgent: string | undefined): ProfileDevice {
  return /iPhone|iPad|iPod|Android|Mobile/i.test(userAgent ?? '') ? 'phone' : 'desktop';
}

export type ProfileCta = 'whatsapp' | 'app' | 'web';

export type ProfileCtaPlan =
  /** `appSub` is the line under the app button: install it, or it opens the one you have. */
  | { layout: 'phone'; ctas: ['whatsapp', 'app'] | ['app', 'whatsapp']; appSub: 'install' | 'open' }
  /** Desktop: "Scan with your phone" QR, then Continue on the web, then WhatsApp (WhatsApp Web). */
  | { layout: 'desktop'; scanQr: true; ctas: ['web', 'whatsapp'] };

/**
 * The fixed CTA ordering (§5.1, wording fixed by the i18n keys):
 * - phone without the app → WhatsApp first: it is one tap, the app path is install + sign-up;
 * - phone with the app likely → the app first;
 * - desktop → scan with your phone, continue on the web, WhatsApp.
 *
 * `appLikely` is false on every SSR today: nothing tells the server an app is installed. A phone WITH the
 * app usually never sees this page at all — the universal link opens the app first (§4.2).
 */
export function profileCtaPlan({ device, appLikely = false }: { device: ProfileDevice; appLikely?: boolean }): ProfileCtaPlan {
  if (device === 'desktop') return { layout: 'desktop', scanQr: true, ctas: ['web', 'whatsapp'] };
  return appLikely
    ? { layout: 'phone', ctas: ['app', 'whatsapp'], appSub: 'open' }
    : { layout: 'phone', ctas: ['whatsapp', 'app'], appSub: 'install' };
}

/** The printed / shared link — `symphony.wix.app`, the host both apps claim (D2). */
export function profilePublicUrl(handle: string, context?: string): string {
  return `https://symphony.wix.app/me/${handle}${context ? `/${context}` : ''}`;
}

/** What a reader would type: the public link without its scheme, printed under every QR. */
export function profileDisplayUrl(handle: string, context?: string): string {
  return profilePublicUrl(handle, context).replace(/^https:\/\//, '');
}

/**
 * The app button's target. `?s=true` is load-bearing: explicit params win over the links service's `/me/`
 * default, so this goes jot → store with `deeplink=me/…` (and opens the app where installed). The bare URL
 * would loop back to this page (§4.2). The card's own `appLink` wins when the backend sends one.
 */
export function profileAppUrl(card: Pick<PublicProfileCard, 'appLink' | 'handle' | 'contextSlug'>): string {
  if (card.appLink && /^https:\/\/symphony\.wix\.app\/me\//.test(card.appLink)) return card.appLink;
  return `${profilePublicUrl(card.handle, card.contextSlug)}?s=true`;
}

/**
 * `wa.me/<number>?text=<prefill>`. The number is the one Symphony inbound number; §6.2 moves it and the
 * pre-fill to one server config when the WhatsApp entry lands (Phase 5).
 *
 * THE PARSER CONTRACT (Phase 5, digest-engine): the short code rides in square brackets at the END of
 * the pre-fill in every locale (`… 👋 [SY-7KQ2]`, `profileLink.wa.prefill`), and the parser takes the LAST
 * bracketed code in the message. The owner's name is interpolated before it and may itself contain
 * brackets, and a visitor may type more after it — "last" is the only rule both survive.
 * test/profile-link-view.test.ts pins that every catalog keeps `[{code}]` last.
 */
export function whatsAppContactUrl(prefill: string, number: string = SYMPHONY_WHATSAPP_INBOUND_NUMBER): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(prefill)}`;
}

/**
 * The web app's root — where the neutral "unavailable" page sends a reader. The profile page's own footer does
 * NOT use it: "Get your own AI assistant" is `profileGetYourOwnUrl`, which carries the owner's referral.
 */
export const SYMPHONY_WEB_URL = 'https://symphony.wix.com/';

/**
 * "Get your own AI assistant" — the OWNER's invite link, `symphony.wix.app/r/<handle>`: the handle IS one of the
 * owner's vanity referral codes (§10), and `r/<code>` is the exact shape every surface emits for a code invite
 * (`lib/referral-share-link.ts` `referralInstallLink`). Through the links service a phone without the app gets
 * the store with the referrer in its deferred deeplink (the app's `referral` jot credits it), a phone WITH the
 * app opens it with the same jot, and a desktop lands on `welcome` — where this page's own capture
 * (`routeReferralToStore`, in localStorage) is what the web sign-up spends. Until 2026-10-23 this was the bare
 * web root, so a visitor who signed up from the footer on a phone credited nobody.
 */
export function profileGetYourOwnUrl(handle: string): string {
  return `https://symphony.wix.app/r/${encodeURIComponent(handle.toLowerCase())}`;
}

export const PRIVACY_URL = 'https://www.wix.com/about/privacy';
export const TERMS_URL = 'https://www.wix.com/about/terms-of-use';

/** TODO(profile-links P7): the in-product report flow (§12.3) replaces this mail link. */
export function reportUrl(handle: string): string {
  return `mailto:abuse@wix.com?subject=${encodeURIComponent(`Symphony profile report: @${handle}`)}`;
}

/**
 * Words that make a display name a BUSINESS / brand rather than a person — "LS Grills", "Acme Studio". Lowercase;
 * matched against whole words of the name.
 */
const BUSINESS_WORDS = new Set([
  'academy',
  'agency',
  'app',
  'bakery',
  'barbershop',
  'bistro',
  'boutique',
  'brand',
  'cafe',
  'café',
  'center',
  'centre',
  'clinic',
  'club',
  'co',
  'co.',
  'company',
  'consulting',
  'corp',
  'corp.',
  'design',
  'designs',
  'digital',
  'events',
  'fitness',
  'foods',
  'gallery',
  'garage',
  'grill',
  'grills',
  'group',
  'gym',
  'home',
  'homes',
  'hotel',
  'inc',
  'inc.',
  'kitchen',
  'lab',
  'labs',
  'llc',
  'ltd',
  'ltd.',
  'market',
  'media',
  'motors',
  'pizza',
  'pizzeria',
  'photography',
  'pro',
  'realty',
  'restaurant',
  'salon',
  'school',
  'services',
  'shop',
  'solutions',
  'spa',
  'store',
  'studio',
  'studios',
  'team',
  'tech',
  'therapy',
  'tours',
  'travel',
  'ventures',
  'wellness',
  'works',
  'yoga',
]);

/** A leading title that belongs to the name it precedes: "Coach Linzi" is not "Coach". Lowercase, dot-less. */
const TITLE_WORDS = new Set([
  'coach',
  'chef',
  'dr',
  'doc',
  'mr',
  'mrs',
  'ms',
  'miss',
  'mx',
  'prof',
  'professor',
  'rabbi',
  'rev',
  'sir',
  'dame',
  'captain',
  'capt',
  'uncle',
  'aunt',
  'auntie',
  'team',
  'the',
]);

/**
 * True when a display name reads as a PERSON's name, so its first word is how to address them ("Ronny Elkayam",
 * "Olivier COISSARD"). False for a business or brand, which must be used whole: a leading acronym ("LS Grills"),
 * a business word anywhere ("Acme Studio"), a leading title ("Coach Linzi", "Dr Who"), digits or symbols
 * ("4 Paws", "Smith & Co").
 */
export function isPersonName(displayName: string): boolean {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return true;
  if (words.length > 4) return false;
  if (/[0-9&@+/|®™]/.test(displayName)) return false;
  const first = words[0];
  // An all-caps first word of 2+ letters is an acronym ("LS", "ABC") — a person's GIVEN name is rarely shouted,
  // whereas a surname in caps is a common French/European convention ("Olivier COISSARD").
  if (first.length >= 2 && first === first.toUpperCase() && first !== first.toLowerCase()) return false;
  if (TITLE_WORDS.has(first.toLowerCase().replace(/\.$/, ''))) return false;
  return !words.some((word) => BUSINESS_WORDS.has(word.toLowerCase()));
}

/**
 * How the page names the owner in copy ("Ronny sees your name…", "Ronny's AI assistant"): a person's first name,
 * or a business's WHOLE name. Cutting "LS Grills" to its first word rendered "Ls's AI assistant" in production.
 */
export function ownerFirstName(displayName: string): string {
  const trimmed = displayName.trim();
  if (!isPersonName(trimmed)) return trimmed;
  const first = trimmed.split(/\s+/)[0];
  return first || trimmed;
}

/** Up to two initials for the avatar fallback. */
export function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => Array.from(part)[0]?.toUpperCase() ?? '')
    .join('');
}

/** WCAG AA body text. The owner's foreground is moved until it clears it against their background (§5.2). */
export const PAGE_TEXT_CONTRAST = 4.5;

/** The page's colours: the owner's three, plus the secondary ink derived from them. */
export interface ProfilePagePalette extends ProfilePalette {
  /** Secondary text (the disclosure, the footer links, "or"): a softer fg that still clears AA on bg. */
  muted: string;
}

/** How far the secondary ink moves from the foreground toward the background before contrast is enforced. */
const MUTED_MIX = 0.35;

const mix = (a: string, b: string, t: number): string => {
  const channel = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  return `#${[0, 1, 2]
    .map((i) =>
      Math.round(channel(a, i) + (channel(b, i) - channel(a, i)) * t)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
};

/**
 * The palette as the page paints it: normalized to `#rrggbb`, foreground adjusted for contrast, never
 * refused — and a secondary ink DERIVED to ≥4.5:1 against the background rather than faded with opacity,
 * which on an owner's palette has no floor at all.
 */
export function profilePagePalette(palette: Partial<ProfilePalette> | null | undefined): ProfilePagePalette {
  const normalized = normalizePalette(palette);
  const fg = adjustToContrast(normalized.fg, normalized.bg, PAGE_TEXT_CONTRAST);
  const muted = adjustToContrast(mix(fg, normalized.bg, MUTED_MIX), normalized.bg, PAGE_TEXT_CONTRAST);
  return { ...normalized, fg, muted };
}

export interface ProfileOgMeta {
  /** The card's own name — the business name for a business card. og:title and twitter:title. */
  name: string;
  /** The headline, or null to use `profileLink.og.description`. */
  headline: string | null;
  /** og:description: the headline, else the bio (cut to a preview's length), else null for the generic line. */
  description: string | null;
  /**
   * Main profile: `index` (goal 4). Context link: `noindex` (§5.5). A fallback renders as the main profile. A card
   * whose name or headline carries markup or a URL is `noindex` too (`hasSuspiciousText`).
   */
  robots: 'index' | 'noindex';
  /** The canonical URL — always the link the card actually is. */
  url: string;
  /** The per-card preview image (`?view=og`, 1200×630). */
  image: string;
}

/** The OG image's size, written into og:image:width/height so an unfurler need not fetch it to lay it out. */
export const PROFILE_OG_W = 1200;
export const PROFILE_OG_H = 630;

const DESCRIPTION_MAX = 200;

/**
 * Markup or a URL in an owner's SHORT fields (name, headline). They are rendered as text, so this is not an XSS
 * guard — it keeps a search engine from indexing a page whose title is a payload or a spam link while the
 * service learns to strip them (defense in depth; a live card carried an HTML payload in its name, 2026-10-23).
 */
export function hasSuspiciousText(...values: string[]): boolean {
  return values.some((value) =>
    /<\s*\/?\s*[a-z!]|javascript:|on[a-z]+\s*=|https?:\/\/|www\.|\b[a-z0-9-]+\.(?:com|net|org|io|ru|xyz|top|link|click|info|biz)\b/i.test(value),
  );
}

/**
 * OG = the card's own name + headline (or bio) + a per-card image drawn from the PUBLIC card (§5.4) — the same
 * renderer as the slide, so there is no photo: an unfurled preview is cached where a later edit cannot reach.
 */
export function profileOgMeta(card: PublicProfileCard, assetOrigin = 'https://symphony.wix.com'): ProfileOgMeta {
  const context = card.fellBackToMain ? undefined : card.contextSlug;
  const headline = card.headline.trim() || null;
  const bio = card.bio.trim().replace(/\s+/g, ' ');
  const description = headline ?? (bio ? (bio.length > DESCRIPTION_MAX ? `${bio.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…` : bio) : null);
  const suspicious = hasSuspiciousText(card.displayName, card.headline);
  return {
    name: card.displayName.trim() || `@${card.handle}`,
    headline,
    description,
    robots: context || suspicious ? 'noindex' : 'index',
    url: profilePublicUrl(card.handle, context),
    image: `${assetOrigin}/me/${card.handle}${context ? `/${context}` : ''}?view=og`,
  };
}

/** The card as the page should treat it: a fallback is the main profile, with no trace of the context. */
export function effectiveCard(card: PublicProfileCard): PublicProfileCard {
  if (!card.fellBackToMain) return card;
  return { ...card, contextSlug: undefined, contextTitle: undefined };
}
